import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Check, ClipboardCheck, X } from 'lucide-react';
import { Botao, Selo } from '../../componentes/base';
import { Estado, Vazio } from '../../componentes/estados';
import { EntradaSensivel, Rotulo } from '../../componentes/formulario';
import { Modal } from '../../componentes/sobreposicao';
import { anunciarOcultos } from '../../componentes/sensivel';
import { TextoCortado } from '../../componentes/texto-cortado';
import { useToast } from '../../componentes/toast';
import { get, mensagemDeErro, post, traduzirErro } from '../../nucleo/api';
import { dataHora, email, nomeDeUsuario, nomePessoa } from '../../nucleo/formato';

/**
 * Lista de 30/10, item 11: Pessoas > Usuários e acessos > Pedidos. A fila de pedidos de troca de e-mail ou de nome de usuário
 * das pessoas do workspace (a decisão é de quem tem `admin.usuarios`). Aprovar o nome de usuário muda na hora; aprovar o e-mail
 * envia o link de confirmação ao endereço novo. Recusar leva um motivo opcional que a pessoa lê em Minha conta. Ninguém decide
 * o próprio pedido, e a trava da conta global (Dono, plataforma, outro workspace) aparece na linha, sem botão.
 */

export interface PedidoDaFila {
  id: string;
  usuario_id: string;
  nome: string;
  email: string | null;
  usuario: string | null;
  tipo: 'email' | 'usuario';
  valor_novo: string;
  status: 'pendente' | 'aprovado' | 'recusado';
  motivo_recusa: string | null;
  decidido_em: string | null;
  decidido_por_nome: string | null;
  created_at: string;
  gerivel: boolean;
  recusa: { code: string; message: string; meta?: Record<string, unknown> } | null;
}
export const CHAVE_FILA_PEDIDOS = ['/auth/pedidos-acesso'];

export function Pedidos() {
  const { t } = useTranslation('admin');
  const qc = useQueryClient();
  const toast = useToast();
  const [recusando, setRecusando] = useState<PedidoDaFila | null>(null);
  const [motivo, setMotivo] = useState('');
  const fila = useQuery({ queryKey: CHAVE_FILA_PEDIDOS, queryFn: () => get<{ itens: PedidoDaFila[] }>('/auth/pedidos-acesso') });
  const atualizar = () => qc.invalidateQueries({ queryKey: CHAVE_FILA_PEDIDOS });
  const aprovar = useMutation({
    mutationFn: (p: PedidoDaFila) => post<{ aguarda_confirmacao: boolean }>(`/auth/pedidos-acesso/${p.id}/aprovar`, {}),
    onSuccess: async (r) => {
      await atualizar();
      toast.sucesso(r.aguarda_confirmacao ? t('usuarios.pedidos.aprovadoEmail') : t('usuarios.pedidos.aprovadoUsuario'));
    },
    onError: async (e) => {
      toast.erro(mensagemDeErro(e));
      await atualizar();
    },
  });
  const recusar = useMutation({
    mutationFn: (p: PedidoDaFila) => post(`/auth/pedidos-acesso/${p.id}/recusar`, motivo.trim() ? { motivo: motivo.trim() } : {}),
    onSuccess: async () => {
      setRecusando(null);
      setMotivo('');
      await atualizar();
      toast.sucesso(t('usuarios.pedidos.recusado'));
    },
    onError: async (e) => {
      toast.erro(mensagemDeErro(e));
      await atualizar();
    },
  });

  return (
    <Estado consulta={fila}>
      {(dados) => {
        const itens = dados.itens;
        const pendentes = itens.filter((p) => p.status === 'pendente');
        const decididos = itens.filter((p) => p.status !== 'pendente');
        if (!itens.length) return <Vazio icone={<ClipboardCheck className="size-5" aria-hidden />} titulo={t('usuarios.pedidos.vazioTitulo')} texto={t('usuarios.pedidos.vazioTexto')} />;
        const linha = (p: PedidoDaFila) => (
          <li key={p.id} className="flex min-w-0 flex-col gap-2 rounded-lg border border-borda p-3" data-pedido={p.status} data-pedido-tipo={p.tipo}>
            <div className="flex min-w-0 flex-wrap items-start justify-between gap-2">
              <div className="min-w-0">
                <TextoCortado className="text-sm font-medium text-tinta-1">{nomePessoa(p.nome)}</TextoCortado>
                <p className="text-xs text-tinta-3">{dataHora(p.created_at)}</p>
              </div>
              {p.status !== 'pendente' && <Selo cor={p.status === 'aprovado' ? 'verde' : 'neutro'}>{t(`usuarios.pedidos.status.${p.status}`)}</Selo>}
            </div>
            <dl className="grid gap-2 text-sm sm:grid-cols-2">
              <div className="min-w-0">
                <dt className="text-xs text-tinta-3">{t(`usuarios.pedidos.atual.${p.tipo}`)}</dt>
                <dd className="numero break-all text-tinta-2">{p.tipo === 'email' ? (p.email ? anunciarOcultos(email(p.email)) : t('usuarios.pedidos.semEmail')) : p.usuario ? anunciarOcultos(nomeDeUsuario(p.usuario)) : '-'}</dd>
              </div>
              <div className="min-w-0">
                <dt className="text-xs text-tinta-3">{t(`usuarios.pedidos.novo.${p.tipo}`)}</dt>
                <dd className="numero break-all text-tinta-1">{anunciarOcultos(p.tipo === 'email' ? email(p.valor_novo) : nomeDeUsuario(p.valor_novo))}</dd>
              </div>
            </dl>
            {p.status === 'pendente' ? (
              <>
                {p.recusa && !p.gerivel && (
                  <p className="text-xs text-tinta-3" data-pedido-recusa={p.recusa.code}>
                    {traduzirErro(p.recusa.code, p.recusa.message, p.recusa.meta)}
                  </p>
                )}
                <div className="flex flex-wrap justify-end gap-2">
                  <Botao variante="fantasma" icone={<X className="size-4" aria-hidden />} disabled={!p.gerivel || recusar.isPending} onClick={() => {
                      setMotivo('');
                      setRecusando(p);
                    }} data-pedido-recusar>
                    {t('usuarios.pedidos.recusar')}
                  </Botao>
                  <Botao icone={<Check className="size-4" aria-hidden />} disabled={!p.gerivel} carregando={aprovar.isPending && aprovar.variables?.id === p.id} onClick={() => aprovar.mutate(p)} data-pedido-aprovar>
                    {t('usuarios.pedidos.aprovar')}
                  </Botao>
                </div>
              </>
            ) : (
              <p className="text-xs text-tinta-3">
                {t('usuarios.pedidos.decidido', { quem: nomePessoa(p.decidido_por_nome ?? ''), quando: p.decidido_em ? dataHora(p.decidido_em) : '' })}
                {p.status === 'recusado' && p.motivo_recusa ? ` ${t('usuarios.pedidos.motivo', { motivo: p.motivo_recusa })}` : ''}
              </p>
            )}
          </li>
        );
        return (
          <div className="flex min-w-0 flex-col gap-4" data-pedidos>
            <p className="text-sm text-tinta-3">{t('usuarios.pedidos.texto')}</p>
            {pendentes.length > 0 ? (
              <ul className="flex flex-col gap-3" aria-label={t('usuarios.pedidos.pendentes')}>
                {pendentes.map(linha)}
              </ul>
            ) : (
              <p className="text-sm text-tinta-2">{t('usuarios.pedidos.nenhumPendente')}</p>
            )}
            {decididos.length > 0 && (
              <>
                <h3 className="text-sm font-semibold text-tinta-1">{t('usuarios.pedidos.recentes')}</h3>
                <ul className="flex flex-col gap-3">{decididos.map(linha)}</ul>
              </>
            )}
            <Modal
              aberto={!!recusando}
              aoFechar={() => {
                setRecusando(null);
                setMotivo('');
              }}
              titulo={t('usuarios.pedidos.recusarTitulo')}
              rodape={
                <>
                  <Botao
                    variante="fantasma"
                    onClick={() => {
                      setRecusando(null);
                      setMotivo('');
                    }}
                  >
                    {t('usuarios.pedidos.cancelar')}
                  </Botao>
                  <Botao variante="perigo" carregando={recusar.isPending} onClick={() => recusando && recusar.mutate(recusando)} data-pedido-confirmar-recusa>
                    {t('usuarios.pedidos.recusar')}
                  </Botao>
                </>
              }
            >
              <Rotulo id="pedido-motivo" rotulo={t('usuarios.pedidos.motivoCampo')} ajuda={t('usuarios.pedidos.motivoAjuda')}>
                <EntradaSensivel mascara={null} livre id="pedido-motivo" maxLength={300} value={motivo} onChange={(e) => setMotivo(e.target.value)} />
              </Rotulo>
            </Modal>
          </div>
        );
      }}
    </Estado>
  );
}
